import { useContext, useRef } from 'react';
import { endOfDay, startOfMonth, subDays } from 'date-fns';

import { TabValue } from '@/types';
import { Refuel } from '@/tmp';
import { DateSelect, Popup, SingleTargetSelector } from '@/components';
import { RefuelHeader } from './RefuelHeader';
import { RefuelDateSelect } from './RefuelDateSelect';
import { RefuelsSummary } from './RefuelsSummary';
import { ctxRefuel } from '@/contexts/RefuelsCtx';

import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { refuels } from '@/tmp/TargetService';
import { ctxPopup } from '@/contexts/PopupContext';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { TargetNameAndDescription, targetNameAndDescriptionStyle } from '@/components/common/TargetNameAndDescription';
import { RefuelData } from './RefuelData';

import './RefuelStyle.css';
import { MinimalTarget } from '@/generated/model';

function RefuelsList() {
  const ctx = useContext(ctxRefuel);

  if (!ctx) return null;

  return (
    <>
      {isPhoneOrHighScreen() ? (
        <div className="refuel-panel-result phone" data-qa-element-id="refuel_result_panel">
          <RefuelsSummary />
          <RefuelData refuel={ctx?.refuels} isListView={true} />
        </div>
      ) : (
        <>
          <RefuelsSummary />
          <RefuelData refuel={ctx?.refuels} isListView={false} />
        </>
      )}
    </>
  );
}

function RefuelMobile() {
  const ctx = useContext(ctxRefuel);

  function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!target) {
      return;
    }
    ctx?.setSelectedTarget(target);
    ctx?.setSelectedDates(undefined);
    ctx?.setTab(TabValue.dateSelect);
  }

  function setRefuelsWrapper(refuels: Refuel[] | undefined) {
    ctx?.setRefuels(refuels);
  }

  function setSelectedDatesWrapper(from: Date, to: Date) {
    ctx?.setSelectedDates({ from, to });
  }

  switch (ctx?.tab) {
    case TabValue.targetSelect:
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={ctx.selectedTarget}
          searchText={ctx.searchText}
          setSearchText={ctx.setSearchText}
        />
      );
    case TabValue.dateSelect:
      return <RefuelDateSelect setRefuels={setRefuelsWrapper} setSelectedDates={setSelectedDatesWrapper} />;
    case TabValue.result:
      return <RefuelsList />;
  }
}

function RefuelDesktop() {
  const ctx = useContext(ctxRefuel);
  const ctxPopups = useContext(ctxPopup);

  const today = new Date();
  const to = endOfDay(subDays(today, 1));
  const from = startOfMonth(to);

  const activeRequestId = useRef(0);

  if (!ctx) return null;

  async function fetchRefuels(target: MinimalTarget, fromDate: Date, toDate: Date) {
    if (!ctx || !target) return;
    const requestId = ++activeRequestId.current;

    ctx.setLoading(true);
    // ilyenkor skeleton noRowsVariant fut
    ctx.setRefuels([]);
    ctxPopups?.showLoader();

    try {
      const result = await refuels(target, fromDate, toDate);

      if (requestId !== activeRequestId.current) return;

      ctx.setRefuels(result.data ?? []);
    } catch (error) {
      if (requestId !== activeRequestId.current) return;
      rethrowIfNotClientException(error);
    } finally {
      if (requestId === activeRequestId.current) {
        ctx.setLoading(false);
      }
      ctxPopups?.hideLoader();
    }
  }

  async function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!ctx || !target) return;

    const fromDate = ctx.selectedDates?.from ?? from;
    const toDate = ctx.selectedDates?.to ?? to;

    ctx.setSelectedTarget(target);
    ctx.setSelectedDates({ from: fromDate, to: toDate });

    await fetchRefuels(target, fromDate, toDate);
  }

  async function setSelectedDatesWrapper(fromDate: Date, toDate: Date) {
    if (!ctx?.selectedTarget) return;

    ctx.setSelectedDates({ from: fromDate, to: toDate });
    await fetchRefuels(ctx.selectedTarget, fromDate, toDate);
  }

  function RefuelsListDesktopHeader() {
    if (!ctx) return null;

    return (
      <>
        <TargetNameAndDescription
          targetName={ctx.selectedTarget?.name}
          targetDescription={ctx.selectedTarget?.description}
          styleClassName={targetNameAndDescriptionStyle(undefined, undefined)}
        />
        <div className="refuel-panel-result-header">
          <DateSelect
            setSelectedDates={setSelectedDatesWrapper}
            currentValue={ctx.selectedDates || { from: null, to: null }}
            disabled={!ctx.selectedTarget}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="refuel-panel-target-selector">
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={ctx.selectedTarget}
          searchText={ctx.searchText}
          setSearchText={ctx.setSearchText}
        />
      </div>
      {ctx.selectedTarget && (
        <div className="refuel-panel-result" data-qa-element-id="refuel_result_panel">
          <RefuelsListDesktopHeader />
          <RefuelsList />
        </div>
      )}
    </>
  );
}

export default function RefuelsPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const ctx = useContext(ctxRefuel);
  if (!ctx) return null;

  return isPhoneOrHighScreen() ? (
    <Popup
      popupTitle={undefined}
      header={<RefuelHeader />}
      body={<RefuelMobile />}
      footer={undefined}
      closePopup={closePopup}
      showCloseButton={false}
      isFullScr={true}
    />
  ) : (
    <RefuelDesktop />
  );
}
